import React, { useState } from 'react';
import { useFinancialYears, useCreateFinancialYear, useLockFinancialYear } from '../hooks/useAccountsCore';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { Lock, Plus, Calendar } from 'lucide-react';
import { format } from 'date-fns';

export function FinancialYearSettings() {
  const { data: years, isLoading } = useFinancialYears();
  const createMutation = useCreateFinancialYear();
  const lockMutation = useLockFinancialYear();

  const [label, setLabel] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createMutation.mutateAsync({ label, startDate, endDate });
      setLabel('');
      setStartDate('');
      setEndDate('');
      setIsCreating(false);
    } catch (err) {
      console.error(err);
    }
  };

  const handleLock = async (id: string) => {
    if (confirm('Are you sure you want to lock this financial year? This cannot be undone.')) {
      await lockMutation.mutateAsync(id);
    }
  };

  if (isLoading) {
    return <Skeleton className="h-[300px] w-full" />;
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-12">
      <Card className="shadow-xs">
        <CardHeader 
          title="Active Financial Years" 
          action={
            <Button size="sm" onClick={() => setIsCreating(true)} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              New Financial Year
            </Button>
          }
        />
        <CardBody className="p-0">
          <div className="divide-y">
            {years?.map((fy) => (
              <div key={fy._id} className="flex items-center justify-between p-4">
                <div className="flex items-center space-x-4">
                  <div className="p-2 bg-primary/10 rounded-full">
                    <Calendar className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">{fy.label}</h3>
                    <p className="text-sm text-muted-foreground">
                      {format(new Date(fy.startDate), 'MMM d, yyyy')} — {format(new Date(fy.endDate), 'MMM d, yyyy')}
                    </p>
                  </div>
                </div>
                <div>
                  {fy.isLocked ? (
                    <span className="flex items-center text-sm text-destructive font-medium">
                      <Lock className="w-4 h-4 mr-1" /> Locked
                    </span>
                  ) : (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleLock(fy._id)}
                      disabled={lockMutation.isPending}
                    >
                      Lock Year
                    </Button>
                  )}
                </div>
              </div>
            ))}
            
            {years?.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No financial years configured.
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {isCreating && (
        <Card className="shadow-xs bg-muted/30">
          <CardHeader title="Create Financial Year" />
          <CardBody className="p-6 space-y-4">
            <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div className="space-y-2">
                <label className="text-sm font-medium">Label (e.g. 2026-27)</label>
                <Input 
                  required 
                  value={label} 
                  onChange={e => setLabel(e.target.value)} 
                  placeholder="2026-27"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Start Date</label>
                <Input 
                  required 
                  type="date" 
                  value={startDate} 
                  onChange={e => setStartDate(e.target.value)} 
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">End Date</label>
                <Input 
                  required 
                  type="date" 
                  value={endDate} 
                  onChange={e => setEndDate(e.target.value)} 
                />
              </div>
              <div className="flex space-x-2">
                <Button type="submit" disabled={createMutation.isPending}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Year
                </Button>
                <Button type="button" variant="outline" onClick={() => setIsCreating(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
